import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../components';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { connectionAgreementsAPI, CreateAgreementRequest } from '../services/connectionAgreementsService';
import { MIN_HOURLY_RATE } from '../config/rateConstants';

function eur(n: number | null | undefined) {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  return `€${Number(n).toFixed(2)}`;
}

import {
  PROFESSIONAL_TYPE_LABELS,
  PROFESSIONAL_CATEGORIES,
  type ProfessionalCategoryId,
} from '../types/professionalRates';

type RateChoice = 'advisory' | 'higher' | 'lower';

interface FormData extends CreateAgreementRequest {
  rate_choice: RateChoice;
  rate_deviation_reason: string;
  assignment_start_date: string;
  inflation_correction_percent: string;
  rate_review_duration_years: string;
}

export const ConnectionAgreementPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const { connectionId } = useParams<{ connectionId: string }>();
  const queryClient = useQueryClient();

  const REPORT_FREQUENCIES = [
    { value: 'every_conversation', label: t('connectionAgreement.reportEveryConversation') },
    { value: 'start_end_only', label: t('connectionAgreement.reportStartEndOnly') },
    { value: 'none', label: t('connectionAgreement.reportNone') },
    { value: 'custom', label: t('connectionAgreement.reportCustom') }
  ];

  const [formData, setFormData] = useState<FormData>({
    professional_function: '',
    hourly_rate: 0,
    rate_choice: 'advisory',
    rate_deviation_reason: '',
    discount_percent: 0,
    max_conversations_per_traject: 5,
    conversation_report_frequency: 'every_conversation',
    custom_report_frequency: '',
    additional_agreements: '',
    assignment_start_date: '',
    inflation_correction_percent: '',
    rate_review_duration_years: '',
  });

  const [advisoryRate, setAdvisoryRate] = useState<number | null>(null);
  const [advisoryLabel, setAdvisoryLabel] = useState<string>('');

  // Get connection details
  useQuery({
    queryKey: ['connection', connectionId],
    queryFn: async () => {
      const response = await api.get(`/professional/connections/${connectionId}`);
      return response.data.data;
    },
    enabled: !!connectionId
  });

  // Get professional profile for KVK date
  const { data: profile } = useQuery({
    queryKey: ['professional-profile'],
    queryFn: async () => {
      const response = await api.get('/professional/profile');
      return response.data.data;
    }
  });

  // Get existing agreement if editing
  const { data: existingAgreement } = useQuery({
    queryKey: ['connection-agreement', connectionId],
    queryFn: async () => {
      if (!connectionId) return null;
      return await connectionAgreementsAPI.getAgreement(parseInt(connectionId));
    },
    enabled: !!connectionId,
    ...({ onSuccess: (data: any) => {
      if (data) {
        setFormData({
          professional_function: data.professional_function,
          hourly_rate: data.hourly_rate,
          rate_choice: (data.rate_choice as RateChoice) || 'advisory',
          rate_deviation_reason: data.rate_deviation_reason || '',
          discount_percent: data.discount_percent || 0,
          max_conversations_per_traject: data.max_conversations_per_traject,
          conversation_report_frequency: data.conversation_report_frequency,
          custom_report_frequency: data.custom_report_frequency || '',
          additional_agreements: data.additional_agreements || '',
          assignment_start_date: data.assignment_start_date || '',
          inflation_correction_percent: data.inflation_correction_percent != null ? String(data.inflation_correction_percent) : '',
          rate_review_duration_years: data.rate_review_duration_years != null ? String(data.rate_review_duration_years) : '',
        });
      }
    } } as any)
  });

  // Fetch advisory rate when function or KVK date changes
  useEffect(() => {
    const fetchAdvisoryRate = async () => {
      if (!formData.professional_function || !profile?.kvk_registration_date) {
        setAdvisoryRate(null);
        setAdvisoryLabel('');
        return;
      }

      try {
        const kvkDate = new Date(profile.kvk_registration_date);
        const today = new Date();
        let years = today.getFullYear() - kvkDate.getFullYear();
        const monthDiff = today.getMonth() - kvkDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < kvkDate.getDate())) {
          years--;
        }
        years = Math.max(0, years);

        let experienceLevel = '0-2';
        if (years >= 10) experienceLevel = '10+';
        else if (years >= 5) experienceLevel = '5-10';
        else if (years >= 2) experienceLevel = '2-5';

        const response = await api.get(
          `/professional-rates/${formData.professional_function}/${experienceLevel}`
        );

        const rate = response.data.data;
        if (rate) {
          setAdvisoryRate(Number(rate.base_rate));
          setAdvisoryLabel(`${years} jaar KVK inschrijving — ervaringsniveau ${experienceLevel}`);
          // Prefill rate if accepting advisory (actuele keuze via prev, niet de closure)
          setFormData(prev => (prev.rate_choice === 'advisory' ? { ...prev, hourly_rate: Number(rate.base_rate) } : prev));
        }
      } catch {
        setAdvisoryRate(null);
        setAdvisoryLabel('');
      }
    };

    fetchAdvisoryRate();
  }, [formData.professional_function, profile?.kvk_registration_date]);

  // When rate_choice switches to advisory, snap hourly_rate to advisoryRate
  useEffect(() => {
    if (formData.rate_choice === 'advisory' && advisoryRate !== null) {
      setFormData(prev => ({ ...prev, hourly_rate: advisoryRate }));
    }
  }, [formData.rate_choice, advisoryRate]);

  const buildPayload = () => ({
    professional_function: formData.professional_function,
    rate_choice: formData.rate_choice,
    hourly_rate: formData.rate_choice === 'advisory' ? (advisoryRate ?? formData.hourly_rate) : formData.hourly_rate,
    rate_deviation_reason: formData.rate_choice !== 'advisory' ? formData.rate_deviation_reason : undefined,
    discount_percent: formData.discount_percent || 0,
    max_conversations_per_traject: formData.max_conversations_per_traject,
    conversation_report_frequency: formData.conversation_report_frequency,
    custom_report_frequency: formData.custom_report_frequency,
    additional_agreements: formData.additional_agreements,
    assignment_start_date: formData.assignment_start_date || undefined,
    inflation_correction_percent: formData.inflation_correction_percent !== ''
      ? parseFloat(formData.inflation_correction_percent)
      : undefined,
    rate_review_duration_years: formData.rate_review_duration_years !== ''
      ? parseInt(formData.rate_review_duration_years)
      : undefined,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!connectionId) throw new Error('Connection ID is required');
      return await connectionAgreementsAPI.createAgreement(parseInt(connectionId), buildPayload() as any);
    },
    onSuccess: () => {
      toast.success(t('connectionAgreement.createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['connection-agreement', connectionId] });
      navigate('/professional/connections');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('connectionAgreement.createError'));
    }
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!connectionId) throw new Error('Connection ID is required');
      return await connectionAgreementsAPI.updateAgreement(parseInt(connectionId), buildPayload() as any);
    },
    onSuccess: () => {
      toast.success(t('connectionAgreement.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['connection-agreement', connectionId] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('connectionAgreement.updateError'));
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.professional_function) {
      toast.error(t('connectionAgreement.functionRequired'));
      return;
    }
    if (formData.rate_choice !== 'advisory') {
      if (!formData.hourly_rate || formData.hourly_rate < MIN_HOURLY_RATE) {
        toast.error(t('connectionAgreement.rateRequired'));
        return;
      }
      if (!formData.rate_deviation_reason.trim()) {
        toast.error(t('connectionAgreement.justificationRequired'));
        return;
      }
    }

    if (existingAgreement) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  if (!connectionId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600">{t('connectionAgreement.missingConnectionId')}</p>
            <Button onClick={() => navigate('/professional/connections')} className="mt-4">
              {t('connectionAgreement.backToConnections')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {existingAgreement ? t('connectionAgreement.titleEdit') : t('connectionAgreement.titleNew')}
        </h1>
        <p className="mt-2 text-gray-600">
          {existingAgreement ? t('connectionAgreement.subtitleEdit') : t('connectionAgreement.subtitleNew')}
        </p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-8">

          {/* Discount request from employer */}
          {(existingAgreement as any)?.discount_requested && (
            <div className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
              <div className="text-sm font-medium text-orange-900">{t('connectionAgreement.discountRequested')}</div>
              <div className="mt-1 text-sm text-orange-800 whitespace-pre-wrap">
                {(existingAgreement as any).discount_request_reason || '—'}
              </div>
              <div className="mt-2 text-xs text-orange-800">
                {t('connectionAgreement.discountResponseHint')}
              </div>
            </div>
          )}

          {/* ── SECTIE 1: Functie ── */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('connectionAgreement.section1')}</h2>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('connectionAgreement.functionLabel')}
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.professional_function}
              onChange={(e) => setFormData({ ...formData, professional_function: e.target.value })}
              required
            >
              <option value="">{t('connectionAgreement.functionPlaceholder')}</option>
              {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => (
                <optgroup key={catId} label={cat.label}>
                  {cat.types.map(type => (
                    <option key={type} value={type}>{PROFESSIONAL_TYPE_LABELS[type]}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* ── SECTIE 2: Uurtarief ── */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('connectionAgreement.section2')}</h2>

            {/* Advisory rate info box */}
            {advisoryRate !== null ? (
              <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm font-semibold text-blue-900 mb-1">
                  {t('connectionAgreement.advisoryRateTitle')}
                </div>
                <div className="text-2xl font-bold text-blue-900">{eur(advisoryRate)}</div>
                <div className="mt-1 text-xs text-blue-700">{advisoryLabel}</div>
                <p className="mt-2 text-xs text-blue-800">
                  {t('connectionAgreement.advisoryRateNote')}
                </p>
              </div>
            ) : formData.professional_function && !profile?.kvk_registration_date ? (
              <div className="mb-5 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                {t('connectionAgreement.noKvkDate')}
              </div>
            ) : null}

            {/* 3-choice radio */}
            <div className="space-y-4">

              {/* Optie 1: Adviestarief overnemen */}
              <label className={`block p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                formData.rate_choice === 'advisory'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="rate_choice"
                    value="advisory"
                    checked={formData.rate_choice === 'advisory'}
                    onChange={() => setFormData({ ...formData, rate_choice: 'advisory' })}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-gray-900">
                      {t('connectionAgreement.option1Label')}
                      {advisoryRate !== null && (
                        <span className="ml-2 text-blue-700 font-semibold">{eur(advisoryRate)}</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-0.5">
                      {t('connectionAgreement.option1Desc')}
                    </div>
                  </div>
                </div>
              </label>

              {/* Optie 2a: Hoger tarief */}
              <label className={`block p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                formData.rate_choice === 'higher'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="rate_choice"
                    value="higher"
                    checked={formData.rate_choice === 'higher'}
                    onChange={() => setFormData({ ...formData, rate_choice: 'higher' })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{t('connectionAgreement.option2Label')}</div>
                    <div className="text-sm text-gray-600 mt-0.5">
                      {t('connectionAgreement.option2Desc')}
                    </div>

                    {formData.rate_choice === 'higher' && (
                      <div className="mt-3 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('connectionAgreement.hourlyRateLabel')}
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">€</span>
                            <input
                              type="number"
                              step="0.50"
                              min={Math.max(MIN_HOURLY_RATE, advisoryRate ?? 0)}
                              className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={formData.hourly_rate || ''}
                              onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                              required
                            />
                          </div>
                          {advisoryRate !== null && (formData.hourly_rate ?? 0) > 0 && (formData.hourly_rate ?? 0) <= advisoryRate && (
                            <p className="mt-1 text-xs text-orange-600">
                              {t('connectionAgreement.rateTooHigh', { rate: eur(advisoryRate) })}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('connectionAgreement.justificationLabel')}
                          </label>
                          <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            value={formData.rate_deviation_reason}
                            onChange={(e) => setFormData({ ...formData, rate_deviation_reason: e.target.value })}
                            placeholder={t('connectionAgreement.justificationPlaceholderHigher')}
                            required
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </label>

              {/* Optie 2b: Lager tarief */}
              <label className={`block p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                formData.rate_choice === 'lower'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="rate_choice"
                    value="lower"
                    checked={formData.rate_choice === 'lower'}
                    onChange={() => setFormData({ ...formData, rate_choice: 'lower' })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{t('connectionAgreement.option3Label')}</div>
                    <div className="text-sm text-gray-600 mt-0.5">
                      {t('connectionAgreement.option3Desc')}
                    </div>

                    {formData.rate_choice === 'lower' && (
                      <div className="mt-3 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('connectionAgreement.hourlyRateLabel')}
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">€</span>
                            <input
                              type="number"
                              step="0.50"
                              min={MIN_HOURLY_RATE}
                              max={advisoryRate ?? undefined}
                              className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={formData.hourly_rate || ''}
                              onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                              required
                            />
                          </div>
                          {advisoryRate !== null && (formData.hourly_rate ?? 0) > 0 && (formData.hourly_rate ?? 0) >= advisoryRate && (
                            <p className="mt-1 text-xs text-orange-600">
                              {t('connectionAgreement.rateTooLow', { rate: eur(advisoryRate) })}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('connectionAgreement.justificationLabel')}
                          </label>
                          <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={3}
                            value={formData.rate_deviation_reason}
                            onChange={(e) => setFormData({ ...formData, rate_deviation_reason: e.target.value })}
                            placeholder={t('connectionAgreement.justificationPlaceholderLower')}
                            required
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </label>
            </div>

            {/* Tarief samenvatting */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('connectionAgreement.advisoryRateRow')}</span>
                <span className="font-medium">{advisoryRate !== null ? eur(advisoryRate) : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('connectionAgreement.yourRateRow')}</span>
                <span className="font-semibold text-gray-900">
                  {formData.rate_choice === 'advisory' ? eur(advisoryRate) : eur(formData.hourly_rate)}
                </span>
              </div>
              <p className="text-xs text-gray-500 pt-1">
                {t('connectionAgreement.invoiceNote')}
              </p>
            </div>
          </div>

          {/* ── SECTIE 3: Tarief afspraken ── */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('connectionAgreement.section3')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('connectionAgreement.startDateLabel')}
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.assignment_start_date}
                  onChange={(e) => setFormData({ ...formData, assignment_start_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('connectionAgreement.durationLabel')}
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.rate_review_duration_years}
                  onChange={(e) => setFormData({ ...formData, rate_review_duration_years: e.target.value })}
                >
                  <option value="">{t('connectionAgreement.durationPlaceholder')}</option>
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{t('connectionAgreement.durationYear', { n })}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {t('connectionAgreement.durationNote')}
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('connectionAgreement.inflationLabel')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.1"
                    min={0}
                    max={20}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.inflation_correction_percent}
                    onChange={(e) => setFormData({ ...formData, inflation_correction_percent: e.target.value })}
                    placeholder={t('connectionAgreement.inflationPlaceholder')}
                  />
                  <span className="text-gray-500 text-sm">%</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {t('connectionAgreement.inflationNote')}
                </p>
              </div>
            </div>
          </div>

          {/* ── SECTIE 4: Overige afspraken ── */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('connectionAgreement.section4')}</h2>

            {/* Max Conversations */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('connectionAgreement.maxConversationsLabel')}
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.max_conversations_per_traject}
                onChange={(e) => setFormData({ ...formData, max_conversations_per_traject: parseInt(e.target.value) })}
                required
              >
                {[1, 2, 3, 4, 5].map(num => (
                  <option key={num} value={num}>{num} {num !== 1 ? t('connectionAgreement.conversationPlural') : t('connectionAgreement.conversationSingular')}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {t('connectionAgreement.maxConversationsNote')}
              </p>
            </div>

            {/* Report Frequency */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('connectionAgreement.reportFrequencyLabel')}
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.conversation_report_frequency}
                onChange={(e) => setFormData({
                  ...formData,
                  conversation_report_frequency: e.target.value as any,
                  custom_report_frequency: e.target.value !== 'custom' ? '' : formData.custom_report_frequency
                })}
                required
              >
                {REPORT_FREQUENCIES.map(freq => (
                  <option key={freq.value} value={freq.value}>{freq.label}</option>
                ))}
              </select>
            </div>

            {formData.conversation_report_frequency === 'custom' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('connectionAgreement.customFrequencyLabel')}
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={formData.custom_report_frequency || ''}
                  onChange={(e) => setFormData({ ...formData, custom_report_frequency: e.target.value })}
                  placeholder={t('connectionAgreement.customFrequencyPlaceholder')}
                  required
                />
              </div>
            )}

            {/* Additional Agreements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('connectionAgreement.additionalAgreementsLabel')}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                value={formData.additional_agreements || ''}
                onChange={(e) => setFormData({ ...formData, additional_agreements: e.target.value })}
                placeholder={t('connectionAgreement.additionalAgreementsPlaceholder')}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/professional/connections')}
            >
              {t('connectionAgreement.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? t('connectionAgreement.saving')
                : existingAgreement
                ? t('connectionAgreement.saveChanges')
                : t('connectionAgreement.createAgreement')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
